import React, { ReactElement, useEffect, useState } from 'react';

import { Box, Flex, useMantineTheme } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { IconBuildingSkyscraper, IconChevronCompactLeft, IconChevronCompactRight, IconHelp } from '@tabler/icons-react';
import { Branding } from 'components/Branding';
import { NavBarLinkButton } from 'components/SideNavBarMenu/NavBarLinkButton';
import { UserButtonWithMenu } from 'components/SideNavBarMenu/UserButton';
import { useUserContext } from 'contexts/UserContext';
import { ChartAverageIcon, GitbookIcon, Key02Icon, PuzzleIcon } from 'hugeicons-react';
import { AgentIcon } from 'icons/AgentIcon';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserService } from 'services/user-service';
import { DOCS_URL, NAV_BAR_ITEMS, ROUTE_LINKS } from 'shared/constants/route-links';
import { HEADER_HEIGHT, PRIMARY_SHADE_WHITE, SLIDING_ANIMATION_DURATION } from 'shared/constants/theme';
import { UserAccess } from 'shared/interfaces/User';

const OPENED_NAV_BAR_WIDTH = 225;
const CLOSED_NAV_BAR_WIDTH = 60;
const TOGGLE_BUTTON_SIZE = 22;

interface SideNavBarMenuItem {
  link: string | string[];
  to: string;
  label: string;
  icon: ReactElement;
  minAccess: UserAccess;
  customOrganisationExclusive: boolean;
}

const sideNavBarMenuItems: SideNavBarMenuItem[] = [
  {
    link: NAV_BAR_ITEMS.smartAssistants,
    to: ROUTE_LINKS.smartAssistants,
    label: 'APP.DASHBOARD_SECTIONS.SMART_ASSISTANTS',
    icon: <AgentIcon w={20} h={20} />,
    minAccess: UserAccess.MEMBER,
    customOrganisationExclusive: false
  },
  {
    link: NAV_BAR_ITEMS.resources,
    to: ROUTE_LINKS.resources,
    label: 'APP.DASHBOARD_SECTIONS.RESOURCES',
    icon: <GitbookIcon size={20} />,
    minAccess: UserAccess.MEMBER,
    customOrganisationExclusive: false
  },
  {
    link: NAV_BAR_ITEMS.integrations,
    to: ROUTE_LINKS.integrations,
    label: 'APP.DASHBOARD_SECTIONS.INTEGRATIONS',
    icon: <PuzzleIcon size={20} />,
    minAccess: UserAccess.ADMIN,
    customOrganisationExclusive: false
  },
  {
    link: NAV_BAR_ITEMS.usage,
    to: ROUTE_LINKS.usage,
    label: 'APP.DASHBOARD_SECTIONS.USAGE',
    icon: <ChartAverageIcon size={20} />,
    minAccess: UserAccess.MEMBER,
    customOrganisationExclusive: false
  },
  {
    link: NAV_BAR_ITEMS.api,
    to: ROUTE_LINKS.api,
    label: 'APP.DASHBOARD_SECTIONS.API',
    icon: <Key02Icon size={20} />,
    minAccess: UserAccess.ADMIN,
    customOrganisationExclusive: false
  },
  {
    link: [NAV_BAR_ITEMS.organisationUsers, NAV_BAR_ITEMS.organisationTeams],
    to: ROUTE_LINKS.organisationUsers,
    label: 'APP.DASHBOARD_SECTIONS.ORGANISATION',
    icon: <IconBuildingSkyscraper />,
    minAccess: UserAccess.ADMIN,
    customOrganisationExclusive: true
  }
];

export function SideNavBarMenu() {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [user] = useUserContext();

  const [active, setActive] = useState<string | string[]>('smart-assistants');
  const [opened, setOpened] = useLocalStorage({
    key: 'isNavBarOpened',
    getInitialValueInEffect: false,
    defaultValue: true
  });

  useEffect(() => {
    // Getting the active link from the URL as everything after dashboard/ (ex: dashboard/chatbots or dashboard/settings/api)
    const splitPath = location.pathname.split('/');
    const activeLink = splitPath.slice(2).join('/');

    setActive(activeLink);
  }, [location.pathname]);

  const isActive = (link: string | string[]) => {
    if (Array.isArray(link)) {
      return link.some((l) => l === active);
    }
    return link === active;
  };

  const Link = ({ item }: { item: SideNavBarMenuItem }) => {
    // Temporary hardcoded settings for rt7 ; Organisation ID: 6641dd2aec73b9c9e4271b25
    // Only show the agents tab for rt7 members
    if (user.organisation === '6641dd2aec73b9c9e4271b25' && !UserService.hasUserAccessWithMinRole(UserAccess.ADMIN)) {
      if (item.link !== NAV_BAR_ITEMS.smartAssistants) return null;
    }

    if (
      !UserService.hasUserAccessWithMinRole(item.minAccess) ||
      (item.customOrganisationExclusive && !UserService.isCustomOrganisationMember())
    )
      return null;
    const isLinkActive = isActive(item.link);
    return (
      <NavBarLinkButton
        link={{
          label: t(item.label),
          icon: item.icon,
          value: item.to,
          absolute: false
        }}
        isActive={isLinkActive}
        onActiveClickClb={() => setActive(item.link)}
        navBarOpened={opened}
      />
    );
  };

  const ToggleButton = () => {
    const buttonClass = {
      width: TOGGLE_BUTTON_SIZE,
      height: TOGGLE_BUTTON_SIZE,
      color: theme.colors.gray[PRIMARY_SHADE_WHITE]
    };
    return (
      <Box
        sx={{
          transition: 'transform 0.3s ease',
          '&:hover': { transform: 'scale(1.4)' }
        }}>
        {opened ? <IconChevronCompactLeft style={buttonClass} /> : <IconChevronCompactRight style={buttonClass} />}
      </Box>
    );
  };

  return (
    <Flex direction="row" align="center">
      <Flex
        direction="column"
        justify="space-between"
        w={opened ? OPENED_NAV_BAR_WIDTH : CLOSED_NAV_BAR_WIDTH}
        h="100%"
        style={{
          transition: `padding ${SLIDING_ANIMATION_DURATION}ms ease, width ${SLIDING_ANIMATION_DURATION}ms ease`,
          borderRight: `1px solid ${theme.other.gray.border}`
        }}>
        <Flex
          direction="column"
          align="start"
          justify="center"
          w="100%"
          h={HEADER_HEIGHT}
          px={opened ? 25 : 15}
          style={{ borderBottom: `1px solid ${theme.other.gray.border}`, transition: 'all 0.3s ease' }}>
          <Branding
            width={opened ? 100 : 28}
            avatar={!opened}
            style={{
              transition: 'width 0.3s ease'
            }}
            onClick={() => navigate(ROUTE_LINKS.smartAssistants)}
          />
        </Flex>
        <Flex
          style={{ flexGrow: 1, transition: 'all 0.3s ease' }}
          p={opened ? 12 : 10}
          pt={10}
          direction="column"
          gap={5}>
          {sideNavBarMenuItems
            .filter((item) => (item.customOrganisationExclusive ? UserService.isCustomOrganisationMember() : true))
            .map((item, key) => (
              <Link item={item} key={key} />
            ))}
        </Flex>
        <Flex direction="column" gap={5} style={{ transition: 'all 0.3s ease' }} p={opened ? 12 : 10} py={0}>
          {/* Try pro button only used when trial not enabled */}
          {/* <TryProButton navBarOpened={opened} /> */}
          <NavBarLinkButton
            w="100%"
            link={{
              label: t('APP.HELP'),
              icon: <IconHelp style={{ width: opened ? 14 : 20, height: opened ? 14 : 20 }} />,
              absolute: true,
              value: DOCS_URL,
              target: '_blank'
            }}
            size="sm"
            navBarOpened={opened}
            minimalHover
          />
        </Flex>
        <Box mt="xs" p={5} style={{ borderTop: `1px solid ${theme.other.gray.border}` }}>
          <UserButtonWithMenu collapsed={!opened} />
        </Box>
      </Flex>

      <Box
        style={{
          cursor: 'pointer',
          position: 'absolute',
          left: opened ? OPENED_NAV_BAR_WIDTH - TOGGLE_BUTTON_SIZE : CLOSED_NAV_BAR_WIDTH - TOGGLE_BUTTON_SIZE,
          zIndex: 100,
          transition: `left ${SLIDING_ANIMATION_DURATION}ms ease`
        }}
        onClick={() => setOpened(!opened)}>
        <ToggleButton />
      </Box>
    </Flex>
  );
}
